import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ErrorMessage } from '@components/error/src/error-message';
import { Input } from '@components/input';
import { LabelPrimary } from '@components/label';
import { cannotBeNegative } from '@utils/validations/cannot-be-negative';
import { max } from '@utils/validations/max';
import { maxDecimals } from '@utils/validations/max-decimals';

interface VatRateInputProps {
  disabled?: boolean;
  readOnly?: boolean;
  name?: string;
}

/**
 * Reusable VAT rate input component with validation
 * To reduce code duplication across different forms
 */
export const VatRateInput = ({ disabled, name = 'vatRate' }: VatRateInputProps) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const hasError = !!errors[name];

  return (
    <div className='mt-8'>
      <LabelPrimary htmlFor={name} hasError={hasError}>
        {t('offers:admissionVatRateLabel')}
      </LabelPrimary>
      <div className='flex items-center w-48'>
        <Controller
          control={control}
          name={name}
          rules={{
            validate: {
              cannotBeNegative: (value) =>
                cannotBeNegative({
                  value,
                  message: t('offers:admissionVatRateCannotBeNegativeError'),
                }),
              maxDecimals: (value) =>
                maxDecimals({
                  value,
                  message: t('offers:admissionVatRateDecimalsError'),
                  decimals: 2,
                }),
              maxValue: (value) =>
                max({
                  value,
                  maxValue: 100,
                  message: t('offers:admissionVatRateMaxError'),
                }),
            },
          }}
          render={({ field }) => (
            <Input
              id={name}
              type='number'
              min='0'
              max='100'
              step='0.01'
              placeholder='0'
              hasError={hasError}
              disabled={disabled}
              readOnly={disabled}
              {...field}
            />
          )}
        />
        <span className='ml-2 text-secondary'>%</span>
      </div>
      <ErrorMessage name={name} />
      <p className='mt-1 text-xs text-secondary'>{t('offers:admissionVatRateDescription')}</p>
    </div>
  );
};
